import envVar from '../../config/envVars';
import authenticationUtils from '../../utils/authenticationUtils';
import httpGet from '../httpClient/httpGet';
import httpPost from '../httpClient/httpPost';
import httpPut from '../httpClient/httpPut';
import httpDelete from '../httpClient/httpDelete';
import {
  PaymentScheme,
  StudentSchemesAndTransactionsResponse,
  ConcessionOrWaiverUpdateRequest
} from '../../types/paymentScheme';
import {
  FetchPaymentSchemeProps,
  updatePaymentSchemeProps,
  UploadFormFAttachmentPayload
} from '../../types/paymentSchemesType';

const fetchPaymentScheme = async ({
  school,
  academicYear,
  grade
}: FetchPaymentSchemeProps) => {
  return httpGet(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/schools/${school}/payment-schemes/${academicYear}/${grade}`,
    {
      headers: {
        Authorization: `Bearer ${envVar.REACT_APP_PAYMENTS_API_TOKEN}`
      }
    }
  );
};

const updatePaymentScheme = async ({
  school,
  academicYear,
  grade,
  payload
}: updatePaymentSchemeProps) => {
  return httpPost(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/schools/${school}/payment-schemes/${academicYear}/${grade}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
};

const clonePaymentScheme = async ({
  school,
  academicYear,
  grade,
  payload
}: updatePaymentSchemeProps) => {
  return httpPost(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/schools/${school}/academic-year/${academicYear}/grade/${grade}/payment-scheme-master/clone`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
};

const uploadFormFAttachment = async ({
  school,
  academicYear,
  grade,
  files,
  schemeName,
  isNewAdmissionPaymentSchemeAttachments,
  attachmentType,
  addTimeStampInFileName
}: UploadFormFAttachmentPayload) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });
  formData.append('academicYear', academicYear);
  formData.append('attachmentType', attachmentType);
  formData.append(
    'isNewAdmissionPaymentSchemeAttachments',
    String(isNewAdmissionPaymentSchemeAttachments)
  );
  formData.append('schemeName', schemeName);
  formData.append('addTimeStampInFileName', String(addTimeStampInFileName));

  return httpPost(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/schools/${school}/payment-scheme-master/grade/${grade}/upload-attachments`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    },
    formData
  );
};

interface FetchReceiptPdfUrlResponse {
  response: string;
  meta: {
    name: string;
    display_name: string;
    encoding: string;
    'content-type': string;
    public_id: string;
  };
}

export enum ConcessionType {
  DEPENDENT = 'DEPENDENT',
  INDEPENDENT = 'INDEPENDENT'
}

export type FixedConcession = {
  description: string;
  percentage: number;
  appliedOn: string;
  type: ConcessionType;
};

const fetchFixedConcessionsMaster = async (): Promise<FixedConcession[]> => {
  const response = await httpGet(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes/fixed-concessions-master`,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response as unknown as FixedConcession[];
};

const addPaymentSchemes = async (
  paymentSchemes: Partial<PaymentScheme>[]
): Promise<PaymentScheme[]> => {
  const response = await httpPost(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes`,
    { paymentSchemes },
    {
      headers: {
        Authorization: `Bearer ${envVar.REACT_APP_PAYMENTS_API_TOKEN}`
      }
    }
  );
  return response.data;
};

interface CreatePaymentSchemePayload {
  session: string;
  scheme: string;
  particulars: string;
  feePeriod: string;
  component: string;
  amount: number;
  paymentDueDate: string;
  userId: string;
  school: string;
  grade: string;
  division: string;
}

const createPaymentScheme = async (
  payload: CreatePaymentSchemePayload
): Promise<any> => {
  const response = await httpPost(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes/create`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

interface SendPaymentLinkPostParams {
  academicYear: string;
  userId: string;
  sendWelcomeEmail: boolean;
}

const sendPaymentLinkPost = async ({
  academicYear,
  userId,
  sendWelcomeEmail
}: SendPaymentLinkPostParams): Promise<any> => {
  const response = await httpPost(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes/${academicYear}/${userId}/payment-link-post?sendWelcomeEmail=${sendWelcomeEmail}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

interface DeregisterEnachParams {
  userId: string;
  academicYear: string;
}

const deregisterEnach = async ({
  userId,
  academicYear
}: DeregisterEnachParams): Promise<any> => {
  const response = await httpPut(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/students/${userId}/enach/academicYear/${academicYear}/de-register`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

const fetchStudentPaymentSchemesWithMetrics = async (
  studentId: string,
  componentType?: string,
  particulars?: string,
  academicYear?: string
): Promise<StudentSchemesAndTransactionsResponse> => {
  const queryParams = new URLSearchParams();
  if (componentType) queryParams.append('component', componentType);
  if (particulars) queryParams.append('particulars', particulars);
  if (academicYear) queryParams.append('academicYear', academicYear);

  const queryString = queryParams.toString();
  const url = `${
    envVar.REACT_APP_PAYMENTS_API_BASE_URL
  }/payment-schemes/students/${studentId}${
    queryString ? `?${queryString}` : ''
  }`;

  const response = await httpGet(url, {
    headers: {
      Authorization: `Bearer ${envVar.REACT_APP_PAYMENTS_API_TOKEN}`
    }
  });
  return response as unknown as StudentSchemesAndTransactionsResponse;
};

const updateMultipleConcessionsAndWaiversStatus = async (payload: {
  concessionOrWaversUpdateRequests: ConcessionOrWaiverUpdateRequest[];
}): Promise<any> => {
  const response = await httpPut(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes/multiple-concessions-waivers/status`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

const deletePaymentSchemes = async (
  paymentSchemeIds: string[]
): Promise<any> => {
  const response = await httpDelete(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes`,
    { paymentSchemeIds },
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

interface UpdatePendingLateFeesPayload {
  paymentSchemeId: string;
  updatedLateFee: number;
}

const updatePendingLateFees = async (
  payload: UpdatePendingLateFeesPayload[]
): Promise<any> => {
  const response = await httpPut(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes/pending-late-fees`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

interface UpdatePaymentSchemeDiscountsPayload {
  discountType: 'CONCESSIONS' | 'WAIVERS';
  paymentSchemeIds: string[];
  discounts: {
    description: string;
    amount: number;
  }[];
}

const updatePaymentSchemeDiscounts = async (
  payload: UpdatePaymentSchemeDiscountsPayload
): Promise<any> => {
  const response = await httpPut(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes/discounts`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response;
};

interface UpdatePaymentSchemeStatusPayload {
  paymentStatus: string;
  remark: string;
  statusChangeDate: string;
  amount?: number;
}

const updatePaymentSchemeStatus = async (
  paymentSchemeId: string,
  payload: UpdatePaymentSchemeStatusPayload
): Promise<any> => {
  const response = await httpPut(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/payment-schemes/${paymentSchemeId}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

interface CancelPaymentTransactionPayload {
  status: string;
  remark: string;
  statusChangeDate: string;
}

const cancelPaymentTransaction = async (
  receiptNo: string,
  payload: CancelPaymentTransactionPayload
): Promise<any> => {
  const encodedReceiptNo = encodeURIComponent(receiptNo);
  const response = await httpPut(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/transactions/${encodedReceiptNo}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

const sendReceiptPost = async (receiptNo: string): Promise<any> => {
  const encodedReceiptNo = encodeURIComponent(receiptNo);
  const response = await httpPost(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/receipts/send?receiptNo=${encodedReceiptNo}`,
    {},
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

const fetchReceiptPdfUrl = async (
  receiptNo: string
): Promise<FetchReceiptPdfUrlResponse> => {
  const encodedReceiptNo = encodeURIComponent(receiptNo);
  const response = await httpGet(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/receipts?receiptNo=${encodedReceiptNo}`,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response as unknown as FetchReceiptPdfUrlResponse;
};

interface UpdatePaymentTransactionStatusPayload {
  paymentDate: string;
  status: string;
  remark: string;
  failureReason?: string;
  feeDepositedUpto: string;
  statusChangeDate: string;
}

const updatePaymentTransactionStatus = async (
  receiptNo: string,
  payload: UpdatePaymentTransactionStatusPayload
): Promise<any> => {
  const encodedReceiptNo = encodeURIComponent(receiptNo);
  const response = await httpPut(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/transactions/${encodedReceiptNo}`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`
      }
    }
  );
  return response.data;
};

interface UpdateChequeStatusPayload {
  id: string;
  chequeNo: string;
  bank: string;
  branch: string;
  issueDate: string;
}

const updateChequeStatus = async (
  payload: UpdateChequeStatusPayload
): Promise<any> => {
  const response = await httpPut(
    `${envVar.REACT_APP_PAYMENTS_API_BASE_URL}/cheques`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${authenticationUtils.getEasypayToken()}`,
        'Content-Type': 'application/json'
      }
    }
  );
  return response.data;
};

const paymentSchemesApis = {
  fetchPaymentScheme,
  updatePaymentScheme,
  clonePaymentScheme,
  addPaymentSchemes,
  createPaymentScheme,
  sendPaymentLinkPost,
  deregisterEnach,
  fetchStudentPaymentSchemesWithMetrics,
  updateMultipleConcessionsAndWaiversStatus,
  deletePaymentSchemes,
  updatePendingLateFees,
  fetchFixedConcessionsMaster,
  updatePaymentSchemeDiscounts,
  updatePaymentSchemeStatus,
  cancelPaymentTransaction,
  sendReceiptPost,
  fetchReceiptPdfUrl,
  updatePaymentTransactionStatus,
  updateChequeStatus,
  uploadFormFAttachment
};

export default paymentSchemesApis;
